import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { ModalScreens } from 'components/Navigation/types';
import { Txt } from 'components/Txt';
import { useSelector } from 'hooks/useSelector';
import { API_DATE_FORMAT, UI_DATE_FORMAT_SHORT } from 'lib/constants';
import { formatCurrency, formatDate } from 'lib/riskbase';
import { theme } from 'lib/theme';
import React from 'react';
import { View } from 'react-native';
import { useDispatch } from 'react-redux';
import {
  NAVIGATE_MODAL,
  doSetupBillingAdminDeleteDiscount,
} from 'store/actions';
import { selectBillingDiscount } from 'store/remote/selectors';
import { BillingDiscountType } from 'types/Billing';

type Props = {
  itemId: string;
};

export const BillingDiscountCard: React.FC<Props> = ({ itemId }) => {
  const dispatch = useDispatch();

  const discount = useSelector((state) => selectBillingDiscount(state, itemId));

  if (!discount) {
    return null;
  }

  const expiryDateYMD = formatDate(discount.expiryDate, API_DATE_FORMAT);
  const isExpired = expiryDateYMD < formatDate(new Date(), API_DATE_FORMAT);
  const isNoLongerValid =
    isExpired ||
    (discount.type === BillingDiscountType.cost &&
      discount.remainingCost === 0);

  return (
    <CardRow
      icon={isNoLongerValid ? ICONS.CircleExclamationLight : undefined}
      iconColor={theme.colors.red}
      onPress={() =>
        dispatch(
          NAVIGATE_MODAL({
            screen: ModalScreens.SettingsBillingDiscount,
            params: { itemId: discount.id },
          }),
        )
      }
      menuOptions={[
        {
          text: 'Delete Discount',
          icon: ICONS.TrashCanLight,
          destructive: true,
          onPress: () =>
            dispatch(doSetupBillingAdminDeleteDiscount({ id: itemId })),
        },
      ]}
      childrenStyle={{
        flexDirection: 'row',
        alignItems: 'center',
      }}
    >
      <View style={{ flex: 1 }}>
        <Txt>{discount.name}</Txt>
        <Txt>
          <Txt
            size="xsmall"
            style={isExpired ? { color: theme.colors.red } : undefined}
          >
            {isExpired
              ? `Expired ${formatDate(
                  discount.expiryDate,
                  UI_DATE_FORMAT_SHORT,
                )}`
              : `Expires ${formatDate(
                  discount.expiryDate,
                  UI_DATE_FORMAT_SHORT,
                )}`}
          </Txt>
          {!!discount.invoiceCodes && (
            <Txt size="xsmall"> ({discount.invoiceCodes})</Txt>
          )}
        </Txt>
      </View>
      <View>
        {discount.type === BillingDiscountType.cost ? (
          <>
            <Txt
              style={{
                textAlign: 'right',
                color: theme.colors.fieldValueText,
              }}
            >
              {formatCurrency(discount.cost)}
            </Txt>
            <Txt
              style={{
                textAlign: 'right',
                color:
                  discount.remainingCost === 0
                    ? theme.colors.red
                    : theme.colors.fieldValueText,
              }}
              size="xsmall"
            >
              {formatCurrency(discount.remainingCost)}
            </Txt>
          </>
        ) : (
          <Txt
            style={{
              textAlign: 'right',
              color: theme.colors.fieldValueText,
            }}
          >
            {discount.percent}%
          </Txt>
        )}
      </View>
    </CardRow>
  );
};
